import { HD_BODYGRAPH_CHANNELS } from './hd-bodygraph-metadata';
import { HdBodygraphPlanetaryActivation } from './hd-bodygraph.models';
import { hdGatesCountingForChannel, hdPreShiftGates } from '../hd-global-shift';
import {
  HdCycleChannelGateVm,
  HdCycleChannelVariant,
  HdCycleGateActivationVm,
  getHdActivatedGates,
  hdActivationsByGate,
} from './hd-cycle-keynotes.builder';

/**
 * Indeling van een composite/relationship chart (chart 1 + chart 2) in de drie
 * relatie-thema's (spec: Steven 2026-08-13). Anders dan bij cycles is de
 * analyse symmetrisch — beide richtingen tellen:
 *
 * - **Electromagnetic** — ieder draagt precies één poort van een kanaal dat
 *   geen van beiden alleen heeft; samen vormen ze het kanaal.
 * - **Compromise** — de ene heeft het volledige kanaal, de ander één poort
 *   erin: die ene poort wordt gecompromitteerd. `fullChannelChart` zegt wie
 *   het kanaal draagt.
 * - **Dominance** — de ene heeft het volledige kanaal, de ander niets erin.
 * - **Companionship** — beide hebben het volledige kanaal zelf al: herkenning
 *   en vriendschap in plaats van genetische aantrekking tot het verschil.
 *
 * Losse hangende poorten vallen bewust buiten beeld; encounter/renewal- en
 * planeet-groep-keynotes zijn hier niet van toepassing (de gate-VM's dragen
 * ze als null). De gates hergebruiken het cycle-VM-model zodat de bestaande
 * kanaal-blokjes ze direct kunnen renderen: chart 1 = 'natal'-rol (blauw),
 * chart 2 = 'cycle'-rol (groen).
 */

export interface HdCompositeChannelVm {
  readonly channelKey: string;
  readonly variant: HdCycleChannelVariant;
  /** Wie het volledige kanaal draagt (compromise/dominance); null bij electromagnetic. */
  readonly fullChannelChart: 1 | 2 | null;
  readonly gates: readonly [HdCycleChannelGateVm, HdCycleChannelGateVm];
}

export interface HdCompositeKeynotesBreakdown {
  readonly electromagnetics: readonly HdCompositeChannelVm[];
  readonly compromises: readonly HdCompositeChannelVm[];
  readonly dominances: readonly HdCompositeChannelVm[];
  readonly companionships: readonly HdCompositeChannelVm[];
}

/** Zonder relatie-irrelevante keynotes: groep- en encounter-info gaan eruit. */
function stripKeynotes(activations: readonly HdCycleGateActivationVm[]): readonly HdCycleGateActivationVm[] {
  return activations.map(activation => ({ ...activation, planetGroup: null }));
}

export function buildHdCompositeKeynotes(
  chart1Activations: readonly HdBodygraphPlanetaryActivation[],
  chart2Activations: readonly HdBodygraphPlanetaryActivation[],
): HdCompositeKeynotesBreakdown {
  const chart1Gates = getHdActivatedGates(chart1Activations);
  const chart2Gates = getHdActivatedGates(chart2Activations);
  const chart1ByGate = hdActivationsByGate(chart1Activations);
  const chart2ByGate = hdActivationsByGate(chart2Activations);

  const electromagnetics: HdCompositeChannelVm[] = [];
  const compromises: HdCompositeChannelVm[] = [];
  const dominances: HdCompositeChannelVm[] = [];
  const companionships: HdCompositeChannelVm[] = [];

  // Kanaalbreuk na de shift (19/49): poorten van een post-2027-geboorte tellen
  // niet mee voor dat kanaal, ook niet samen met de partner (hd-global-shift.ts).
  const preShift1 = hdPreShiftGates(chart1Activations.filter(activation => chart1Gates.has(activation.gate)));
  const preShift2 = hdPreShiftGates(chart2Activations.filter(activation => chart2Gates.has(activation.gate)));

  for (const channel of HD_BODYGRAPH_CHANNELS) {
    const [gateA, gateB] = channel.gates;
    const counting1 = hdGatesCountingForChannel(channel.key, chart1Gates, preShift1);
    const counting2 = hdGatesCountingForChannel(channel.key, chart2Gates, preShift2);
    const has1A = counting1.has(gateA);
    const has1B = counting1.has(gateB);
    const has2A = counting2.has(gateA);
    const has2B = counting2.has(gateB);

    // Niet samen gevormd → geen relatie-thema.
    if (!((has1A || has2A) && (has1B || has2B))) {
      continue;
    }

    const full1 = has1A && has1B;
    const full2 = has2A && has2B;

    const gateVm = (gate: number, has1: boolean, has2: boolean): HdCycleChannelGateVm => ({
      gate,
      source: has2 ? (has1 ? 'both' : 'cycle') : 'natal',
      cycleActivations: stripKeynotes(chart2ByGate.get(gate) ?? []),
      natalActivations: stripKeynotes(chart1ByGate.get(gate) ?? []),
      encounter: null,
    });
    const vm = (variant: HdCycleChannelVariant, fullChannelChart: 1 | 2 | null): HdCompositeChannelVm => ({
      channelKey: channel.key,
      variant,
      fullChannelChart,
      gates: [gateVm(gateA, has1A, has2A), gateVm(gateB, has1B, has2B)],
    });

    // Beide hebben het kanaal zelf al → companionship: geen aantrekking tot
    // het verschil, wél herkenning (beide poort-chips zijn 'both').
    if (full1 && full2) {
      companionships.push(vm('companionship', null));
      continue;
    }

    if (full1 || full2) {
      const fullChart = full1 ? 1 : 2;
      const otherHasGate = full1 ? (has2A || has2B) : (has1A || has1B);
      if (otherHasGate) {
        compromises.push(vm('compromise', fullChart));
      } else {
        dominances.push(vm('dominance', fullChart));
      }
      continue;
    }
    // Geen van beiden volledig, wél samen gevormd → electromagnetic.
    electromagnetics.push(vm('electromagnetic', null));
  }

  return { electromagnetics, compromises, dominances, companionships };
}
